import {
    throttle,
    select,
    put,
    takeLatest,
    all,
    fork,
    call,
} from 'redux-saga/effects'

import { LOCAL_SPEAKER_THRESHOLD } from 'constants/config'
import { isNil } from 'helpers/lodash'
import { isString } from 'helpers/miscellaneous'
import { isTeacher, getInfoFromIdentity } from 'helpers/twilio'
import {
    unsubscribeFromAllParticipantsVideo,
    unsubscribeFromAllParticipantsVideoExceptTeacher,
    subscribeToAllParticipantsTracks,
} from 'services/apis/twilioSubscriptions'
import { isDesktop } from 'utils/device'
import { logger } from 'utils/logger'
import PIPUtil from 'utils/PIPUtil'

import {
    handleLocalParticipantConnected,
    handleRemoteParticipants,
} from './handlers/Room'
import {
    watchParticipantConnection,
    watchParticipantDisconnection,
    watchDominantSpeakerChanged,
} from './watchers/Room'

import { setIsSpeaking, updateDominantSpeaker } from '../actions/Room'
import * as types from '../actionTypes/Room'
import { _IsSpeaking, _IsMaster, _dominantSpeaker } from '../selectors/Room'
import { _isTeacher, _userInfo } from '../selectors/User'

const computeIsSpeakingNextValue = (isSpeaking, volume) => {
    const nextValue =
        typeof isSpeaking === 'boolean'
            ? isSpeaking
            : volume >= LOCAL_SPEAKER_THRESHOLD

    return nextValue
}

function* _watchIsSpeaking({ isSpeaking, volume }) {
    const nextValue = computeIsSpeakingNextValue(isSpeaking, volume)

    try {
        const isMaster = yield select(_IsMaster)

        // isSpeaking is not relevant if current user is not the master
        // master is the one who sends ttm data via websockets
        if (!isMaster) return

        const isSpeakingCurrentState = yield select(_IsSpeaking)
        // compare both values , if its same just return , otherwise process it
        if (isSpeakingCurrentState === nextValue) return

        // put action to store
        yield put(setIsSpeaking(nextValue))
    } catch (error) {
        console.log(error)
        // TODO: notify or log error or put error action
    }
}

export function* watchIsSpeaking() {
    yield throttle(1000, types.HANDLE_SET_IS_SPEAKING, _watchIsSpeaking)
}

function* _watchHandleLocalDominantSpeaker({ isSpeaking, volume }) {
    const nextValue = computeIsSpeakingNextValue(isSpeaking, volume)

    try {
        const dominantSpeakerId = yield select(_dominantSpeaker)

        if (nextValue) {
            const { user_id } = yield select(_userInfo)

            if (dominantSpeakerId !== user_id) {
                yield put(updateDominantSpeaker(user_id))
            }
        } else {
            // first check here if there is any remote user which was a dominant speaker and still they are
            const remoteDominantSpeaker = window.twilioRoom.dominantSpeaker

            if (remoteDominantSpeaker) {
                const { id } = getInfoFromIdentity(
                    remoteDominantSpeaker.identity
                )

                if (isString(dominantSpeakerId) && dominantSpeakerId !== id) {
                    yield put(updateDominantSpeaker(id))
                }
            } else {
                yield put(updateDominantSpeaker(null))
            }
        }
    } catch (error) {
        console.log(error)
        // TODO: notify or log error or put error action
    }
}

export function* watchHandleLocalDominantSpeaker() {
    yield throttle(
        5000,
        types.HANDLE_SET_IS_SPEAKING,
        _watchHandleLocalDominantSpeaker
    )
}

function* _watchHandlePIP({ enabled }) {
    try {
        const mountElement = document.getElementById('pipVideo')

        if (enabled) {
            yield PIPUtil.enterPIP(mountElement)
        } else {
            yield PIPUtil.exitPIP(mountElement)
        }
    } catch (error) {
        console.log(error)
        // TODO: notify or log error or put error action
    }
}

export function* watchHandlePIP() {
    yield takeLatest(types.HANDLE_PIP, _watchHandlePIP)
}

function* _watchHandleRoomState({ room }) {
    try {
        const { localParticipant, participants } = room

        if (!isDesktop) {
            let teacherSid = null

            for (let participant of participants.values()) {
                if (isTeacher(participant.identity)) {
                    teacherSid = participant.sid
                    break
                }
            }

            if (teacherSid) {
                yield unsubscribeFromAllParticipantsVideoExceptTeacher(
                    room.sid,
                    room.localParticipant.sid,
                    teacherSid
                )
            } else {
                yield unsubscribeFromAllParticipantsVideo(
                    room.sid,
                    room.localParticipant.sid
                )
            }
        }

        // handle local participant
        yield* handleLocalParticipantConnected(localParticipant)

        // handle remote participant
        yield* handleRemoteParticipants(participants)

        /**
         * attach watchers to all the subscription channels
         */
        yield all([
            fork(watchParticipantConnection, room),
            fork(watchParticipantDisconnection, room),
            fork(watchDominantSpeakerChanged, room),
        ])
    } catch (error) {
        logger.error(`error in handling room state :- ${error}`)
    }
}

export function* watchHandleRoomState() {
    yield takeLatest(types.HANDLE_ROOM_STATE, _watchHandleRoomState)
}

function* _watchHandleFullScreenToggle({ isTeacherFullScreen }) {
    try {
        if (isDesktop && window.twilioRoom) {
            const { participants } = window.twilioRoom

            if (isTeacherFullScreen) {
                // next state  will be full screen off, so revert to normal state
                yield call(
                    subscribeToAllParticipantsTracks,
                    window.twilioRoom.sid,
                    window.twilioRoom.localParticipant.sid
                )
            } else {
                // next state  will be full screen on, so switch off all videos
                const isLocalUserIsTeacher = yield select(_isTeacher)

                if (isLocalUserIsTeacher) {
                    // unsubscribe from all videos
                    unsubscribeFromAllParticipantsVideo(
                        window.twilioRoom.sid,
                        window.twilioRoom.localParticipant.sid
                    )
                } else {
                    let teacherSid = null

                    for (let participant of participants.values()) {
                        if (isTeacher(participant.identity)) {
                            teacherSid = participant.sid
                            break
                        }
                    }

                    if (isNil(teacherSid)) return

                    yield call(
                        unsubscribeFromAllParticipantsVideoExceptTeacher,
                        window.twilioRoom.sid,
                        window.twilioRoom.localParticipant.sid,
                        teacherSid
                    )
                }
            }
        }
    } catch (error) {
        logger.error(`error in handling full screen toggle :- ${error}`)
    }
}

export function* watchHandleFullScreenToggle() {
    yield takeLatest(
        types.HANDLE_FULL_SCREEN_TOGGLE,
        _watchHandleFullScreenToggle
    )
}
